/**
 * Helpers for floating sheet images (content coordinates + overlay mapping).
 */

export function generateImageId() {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `img-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/**
 * Map content-space (grid) coordinates to overlay pixel position for the main body area.
 * Mirrors DataProxy#getRect scroll/freeze adjustment for a point.
 */
export function contentPointToOverlay(data, contentX, contentY) {
  if (!data) return { left: 0, top: 0 };
  const { scroll } = data;
  const left = contentX;
  const top = contentY;
  let left0 = left - scroll.x;
  let top0 = top - scroll.y;
  const fsw = data.freezeTotalWidth();
  const fsh = data.freezeTotalHeight();
  if (fsw > 0 && fsw > left) {
    left0 = left;
  }
  if (fsh > 0 && fsh > top) {
    top0 = top;
  }
  return { left: left0, top: top0 };
}

export function clampImageDimensions(naturalW, naturalH, maxW = 360, maxH = 420) {
  let w = naturalW;
  let h = naturalH;
  if (!w || !h) return { width: 160, height: 120 };
  if (w > maxW) {
    h = (h * maxW) / w;
    w = maxW;
  }
  if (h > maxH) {
    w = (w * maxH) / h;
    h = maxH;
  }
  return { width: Math.round(w), height: Math.round(h) };
}

export function notifySheetImagesMutated() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new Event('milegrid-images-mutated'));
  }
}

/** Dispatched with `detail: { file: File }` so SpreadsheetImageUploadBridge can Cloudinary-upload then insert. */
export const SHEET_IMAGE_FILE_SELECTED = 'milegrid-sheet-image-selected';

export function dispatchSheetImageFileSelected(file) {
  if (typeof window === 'undefined' || !file) return;
  window.dispatchEvent(new CustomEvent(SHEET_IMAGE_FILE_SELECTED, { detail: { file } }));
}

/** Toolbar insert-image listens for in-button spinner + inline status text. */
export const SHEET_IMAGE_UPLOAD_UI = 'milegrid-sheet-image-upload-ui';

/** @param {{ active: boolean, message?: string }} detail */
export function dispatchSheetImageUploadUi(detail) {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SHEET_IMAGE_UPLOAD_UI, { detail: detail || {} }));
}

/**
 * Insert a new image anchored to the current selection's top-left cell.
 * Uses changeData so undo/history and debounced save stay consistent.
 * @param {string} src - HTTPS URL (e.g. Cloudinary) or data URL
 */
export function insertImageFromDataUrl(spreadsheetInstanceRef, src, naturalWidth, naturalHeight) {
  const inst = spreadsheetInstanceRef?.current;
  const sheet = inst?.sheet;
  const data = sheet?.data;
  if (!sheet || !data || data.settings?.mode === 'read') return null;

  const { sri, sci } = data.selector.range;
  const rect = data.cellRect(sri, sci);
  const { width, height } = clampImageDimensions(naturalWidth, naturalHeight);

  const image = {
    id: generateImageId(),
    src,
    x: rect.left + 2,
    y: rect.top + 2,
    width,
    height,
    sheetId: data.name,
    createdAt: new Date().toISOString(),
  };

  data.changeData(() => {
    const prev = Array.isArray(data.images) ? data.images : [];
    data.images = [...prev, image];
  });

  sheet.table?.render?.();
  notifySheetImagesMutated();
  return image;
}

export function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export function loadNaturalSizeFromDataUrl(dataUrl) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: 200, height: 150 });
    img.src = dataUrl;
  });
}

/** Natural dimensions for a remote URL or data URL (sheet images after Cloudinary upload). */
export function loadNaturalSizeFromUrl(src) {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve({ width: img.naturalWidth, height: img.naturalHeight });
    img.onerror = () => resolve({ width: 200, height: 150 });
    img.src = src;
  });
}
